import { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { colors } from "brand";
import { useTranslation } from "i18n-fe";
import { userDisplayName } from "common-fe";
import JudgeSignatureModal from "./JudgeSignatureModal";

// #732 — per-judge drawer: basic info + signature status, with a button that
// opens the HUGE signature capture modal. Same right-anchored Drawer pattern
// as RideHistoryDrawer (#507) for People/Horses.
export default function JudgeDrawer({ open, onClose, judge, onSignatureChanged }) {
  const { t } = useTranslation("scoreadmin");
  const [modalOpen, setModalOpen] = useState(false);

  const infoItems = judge
    ? [
        { label: t("judges.columns.fvdJudgeId"), value: judge.fvdJudgeId },
        { label: t("judges.columns.role"), value: judge.role },
        { label: t("judges.columns.country"), value: judge.country },
      ]
    : [];

  return (
    <>
      {/* AppLayout's AppBar (common-fe) is pinned at zIndex 1300 — same as MUI's
          default Modal z-index Drawer uses — so without bumping this it paints
          over the drawer's title/close button (same fix as RideHistoryDrawer). */}
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 100 }}
      >
        <Box sx={{ width: { xs: "100vw", sm: 480 }, p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h5" color="text.primary">
              {judge ? userDisplayName(judge) : ""}
            </Typography>
            <IconButton onClick={onClose} aria-label={t("judges.drawer.close")}>
              <CloseIcon />
            </IconButton>
          </Box>

          {infoItems.length > 0 && (
            <Box sx={{ mb: 3 }}>
              {infoItems.map((item) => (
                <Typography key={item.label} variant="body2" color="text.secondary">
                  <strong>{item.label}:</strong> {item.value || "-"}
                </Typography>
              ))}
            </Box>
          )}

          <Divider sx={{ mb: 2 }} />

          <Typography variant="h6" gutterBottom color="text.primary">
            {t("judges.drawer.signatureTitle")}
          </Typography>

          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <Chip
              label={
                judge?.signaturePresent
                  ? t("judges.drawer.signatureOnFile")
                  : t("judges.drawer.signatureMissing")
              }
              color={judge?.signaturePresent ? "success" : "default"}
              variant={judge?.signaturePresent ? "filled" : "outlined"}
            />
          </Stack>

          {judge?.signaturePresent && judge?.signatureUrl && (
            <Box
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                bgcolor: "#ffffff",
                p: 1,
                mb: 2,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Box
                component="img"
                src={judge.signatureUrl}
                alt={t("judges.drawer.signatureTitle")}
                sx={{ maxWidth: "100%", maxHeight: 120 }}
              />
            </Box>
          )}

          {/* #732 — brand's default "primary" outlined button is nearly
              invisible in dark mode (dark-grey on dark-grey); force
              foxOrange like other score FEs' primary actions. */}
          <Button
            variant="outlined"
            onClick={() => setModalOpen(true)}
            disabled={!judge}
            sx={{
              color: colors.foxOrange,
              borderColor: colors.foxOrange,
              "&:hover": { borderColor: colors.foxOrange, bgcolor: "action.hover" },
            }}
          >
            {judge?.signaturePresent
              ? t("judges.drawer.manageSignature")
              : t("judges.drawer.captureSignature")}
          </Button>
        </Box>
      </Drawer>

      <JudgeSignatureModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        judge={judge}
        onSaved={() => {
          setModalOpen(false);
          onSignatureChanged?.();
        }}
      />
    </>
  );
}
